// Status pills and plain labels for the data-table controller (`data-format="pill:<name>"` / `label:<name>`).
// A map is either { <value>: { text, cls } } or a function (value, item) -> { text, cls }.
// `cls` is an existing `.ls-pill--*` modifier from tailwind.css ("" = the neutral grey pill).
// Values are the serializers' real TextChoices values; Polish labels mirror the models.

import { formatMoney } from "./data_table_formats.js";

const WAITING = "ls-pill--waiting";
const ACTIVE = "ls-pill--active";

function paymentStatus(_value, p) {
  if (p.late) return { text: "Spóźniona wpłata", cls: "ls-pill--late" };
  if (p.status === "pending" && p.method === "p24") {
    return { text: "Czeka na potwierdzenie P24", cls: "ls-pill--pending_p24" };
  }
  if (p.status === "pending") return { text: "Oczekująca", cls: "" };
  if (p.status === "paid") return { text: "Opłacona", cls: "ls-pill--paid" };
  if (p.status === "failed") return { text: "Nieudana", cls: "ls-pill--failed" };
  return { text: p.status ? humanize(p.status) : "—", cls: "" };
}

// Staff payments list: what the payment was for (an order row links to „Kupione leady" via the column href) …
function paymentWhat(_value, p) {
  return p.kind === "order"
    ? { text: `Zamówienie #${p.order_code}`, cls: "" }
    : { text: "Doładowanie portfela", cls: "" };
}

// … and the refunds cell: pending count first, else the refunded sum, else a dash.
function paymentRefunds(_value, p) {
  if (p.refunds_pending_count > 0) return { text: `${p.refunds_pending_count} do wykonania`, cls: "" };
  if (p.refunds_total_pln && p.refunds_total_pln !== "0.00") {
    return { text: `${formatMoney(p.refunds_total_pln)} zwrócono`, cls: "" };
  }
  return { text: "—", cls: "" };
}

const THREAD_STATUS = {
  new:            { text: "Nowe",                  cls: WAITING },
  awaiting_reply: { text: "Oczekuje na odpowiedź", cls: "ls-pill--refund_todo" },
  answered:       { text: "Odpowiedziano",         cls: ACTIVE },
  closed:         { text: "Zamknięte",             cls: "ls-pill--failed" },
};

function threadStatus(value, t) {
  const known = THREAD_STATUS[value];
  if (known) return t.status_label ? { ...known, text: t.status_label } : known;
  return { text: t.status_label || humanize(value), cls: "" };
}

// Staff clients list (organisation row of /api/staff/clients/).
function clientPurchasing(_value, c) {
  if (c.purchasing_blocked) return { text: "Zablokowane", cls: "ls-pill--blocked" };
  if (c.purchasing_enabled) return { text: "Aktywne", cls: ACTIVE };
  return { text: "Czeka na aktywację", cls: WAITING };
}

function ownerVerification(_value, c) {
  const owner = c.owner;
  if (!owner) return null;
  if (owner.email_verified && owner.phone_verified) return { text: "Zweryfikowany", cls: ACTIVE };
  if (owner.email_verified) return { text: "Tylko e-mail", cls: WAITING };
  return { text: "Niezweryfikowany", cls: WAITING };
}

// Real values from CompanyCheck.Status (kyc/models/company_check.py); null = never checked.
function gusStatus(value) {
  if (value === "valid") return { text: "Potwierdzona", cls: "ls-pill--gus-ok" };
  if (value === "not_found") return { text: "Nie znaleziono", cls: "ls-pill--gus-bad" };
  if (value === "inactive") return { text: "Zakończona", cls: "ls-pill--gus-bad" };
  if (value === "unavailable") return { text: "GUS niedostępny", cls: "ls-pill--gus-error" };
  if (value === "error") return { text: "Błąd sprawdzenia", cls: "ls-pill--gus-error" };
  return { text: "Nie sprawdzano", cls: "ls-pill--gus-none" };
}

export const PILLS = {
  clientPurchasing,
  ownerVerification,
  gusStatus,
  orderStatus: {
    pending:   { text: "Oczekujące",  cls: WAITING },
    paid:      { text: "Opłacone",    cls: "ls-pill--paid" },
    failed:    { text: "Nieudane",    cls: "ls-pill--failed" },
    delivered: { text: "Dostarczone", cls: ACTIVE },
    refunded:  { text: "Zwrócone",    cls: "ls-pill--refund_todo" },
  },
  invoiceStatus: {
    to_invoice: { text: "Do zafakturowania", cls: WAITING },
    invoiced:   { text: "Zafakturowane",     cls: ACTIVE },
  },
  deliveryStatus: {
    pending:   { text: "W przygotowaniu", cls: WAITING },
    ready:     { text: "Zbudowana",       cls: "ls-pill--pending_p24" },
    delivered: { text: "Dostarczona",     cls: ACTIVE },
    failed:    { text: "Nieudana",        cls: "ls-pill--failed" },
  },
  paymentStatus,
  paymentWhat,
  paymentRefunds,
  leadKind: {
    firma: { text: "Firma", cls: "" },
    osoba: { text: "Osoba", cls: "" },
  },
  complaintStatus: {
    open:     { text: "Otwarta",         cls: WAITING },
    resolved: { text: "Rozstrzygnięta",  cls: ACTIVE },
  },
  complaintResolution: {
    replaced: { text: "Wymieniono",             cls: "" },
    credited: { text: "Zwrot środków (kredyt)", cls: "" },
    rejected: { text: "Odrzucona",              cls: "" },
  },
  taskStatus: {
    success: { text: "Sukces",      cls: "ls-pill--paid" },
    warning: { text: "Ostrzeżenia", cls: "ls-pill--refund_todo" },
    error:   { text: "Błąd",        cls: "ls-pill--late" },
    running: { text: "W toku",      cls: "ls-pill--pending_p24" },
    skipped: { text: "Pominięte",   cls: "ls-pill--failed" },
  },
  threadStatus,
  pocket: {
    deposited: { text: "Wpłacone", cls: ACTIVE },
    bonus:     { text: "Bonus",    cls: WAITING },
  },
  userActive: {
    true:  { text: "Aktywny",    cls: ACTIVE },
    false: { text: "Nieaktywny", cls: WAITING },
  },
  verticalAccess: {
    open:       { text: "Otwarty",   cls: ACTIVE },
    restricted: { text: "Zamknięty", cls: WAITING },
  },
  verticalActive: {
    true:  { text: "Aktywny",    cls: ACTIVE },
    false: { text: "Nieaktywny", cls: WAITING },
  },
  role: {
    owner:  { text: "Właściciel",    cls: "" },
    admin:  { text: "Administrator", cls: "" },
    member: { text: "Członek",       cls: "" },
  },
  paymentMethod: {
    p24:    { text: "Przelewy24", cls: "" },
    wallet: { text: "Portfel",    cls: "" },
    other:  { text: "Inna",       cls: "" },
  },
  caseKind: {
    complaint: { text: "Reklamacja", cls: "" },
    thread:    { text: "Zgłoszenie", cls: "" },
  },
  paymentKind: {
    order:  { text: "Zamówienie",   cls: "" },
    top_up: { text: "Doładowanie",  cls: "" },
  },
};

// "awaiting_reply" -> "Awaiting reply": an unmapped code never shows raw.
export function humanize(value) {
  const text = String(value).replace(/[_.-]+/g, " ").trim();
  return text.charAt(0).toUpperCase() + text.slice(1);
}

// { text, cls } for a value; null when the value is empty. Unknown values show raw, never silently vanish.
export function resolvePill(name, value, item) {
  const map = PILLS[name];
  if (!map) return null;
  if (typeof map === "function") return map(value, item);
  if (value === null || value === undefined || value === "") return null;
  return map[String(value)] || { text: humanize(value), cls: "" };
}
